import React, { useState, useRef, useEffect, useCallback } from "react"
import ReactDOM from "react-dom"
import { useNavigate, useLocation } from "react-router-dom"
import clsx from "clsx"
import styles from "./notificationBell.module.css"
import useOutsideAlerter from "../../../utils/clickOutside"
import { useInstitution } from "../../../context/InstitutionContext"
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from "../../../services/notifications"
import {
  getIconInfo,
  formatTimeAgo,
  getActionPlan
} from "../../notifications/notificationActions"
import NotificationDisciplines from "../../notifications/NotificationDisciplines"
import NotificationMessage from "../../notifications/notificationMessage"

function parseImportError(errorStr) {
  const sections = []

  const lineMatch = errorStr.match(/Erro ao processar o arquivo na linha (\d+)\.\s*/)
  if (lineMatch) {
    sections.push({ type: "line", line: lineMatch[1] })
  }

  const missingMatch = errorStr.match(/Colunas ausentes no arquivo:\s*([^.]+)\./)
  if (missingMatch) {
    sections.push({
      type: "columns",
      label: "Colunas ausentes no arquivo",
      severity: "error",
      columns: missingMatch[1].split(",").map(c => c.trim()).filter(Boolean)
    })
  }

  const unrecognizedMatch = errorStr.match(/Colunas n[aã]o reconhecidas no arquivo:\s*([^.]+)\./)
  if (unrecognizedMatch) {
    sections.push({
      type: "columns",
      label: "Colunas não reconhecidas no arquivo",
      severity: "warning",
      columns: unrecognizedMatch[1].split(",").map(c => c.trim()).filter(Boolean)
    })
  }

  const expectedMatch = errorStr.match(/Verifique se o arquivo possui as colunas corretas:\s*([^.]+)\./)
  if (expectedMatch) {
    sections.push({
      type: "columns",
      label: "Colunas esperadas",
      severity: "info",
      columns: expectedMatch[1].split(",").map(c => c.trim()).filter(Boolean)
    })
  }

  if (sections.length === 0) {
    sections.push({ type: "text", text: errorStr })
  }

  return sections
}

function ImportErrorDetails({ parsedData, message, userId }) {
  if (!parsedData) {
    return <NotificationMessage text={message} className={styles.modalMessage} />
  }

  const errors = parsedData.errors && parsedData.errors.length > 0
    ? parsedData.errors
    : parsedData.errorDetail
      ? [parsedData.errorDetail]
      : null

  if (!errors) {
    return <NotificationMessage text={message} className={styles.modalMessage} />
  }

  return (
    <div className={styles.errorDetails}>
      <div className={styles.errorFileInfo}>
        <div className={styles.errorFileRow}>
          <span className={styles.errorFileLabel}>Arquivo:</span>
          <span className={styles.errorFileName}>{parsedData.fileName || "—"}</span>
        </div>
        {parsedData.category && (
          <div className={styles.errorFileRow}>
            <span className={styles.errorFileLabel}>Categoria:</span>
            <span className={styles.errorFileName}>{parsedData.category}</span>
          </div>
        )}
        {userId && (
          <div className={styles.errorFileRow}>
            <span className={styles.errorFileLabel}>Usuário:</span>
            <span className={styles.errorFileName}>{userId}</span>
          </div>
        )}
      </div>

      {errors.map((err, idx) => {
        const sections = parseImportError(err)
        return (
          <div key={idx} className={styles.errorBlock}>
            {errors.length > 1 && (
              <p className={styles.errorBlockTitle}>Erro {idx + 1}</p>
            )}
            {sections.map((section, sIdx) => {
              if (section.type === "line") {
                return (
                  <p key={sIdx} className={styles.errorLineInfo}>
                    Erro encontrado na <strong>linha {section.line}</strong> do arquivo
                  </p>
                )
              }
              if (section.type === "columns") {
                return (
                  <div key={sIdx} className={styles.errorSection}>
                    <p className={clsx(
                      styles.errorSectionLabel,
                      styles[`severity_${section.severity}`]
                    )}>
                      {section.label}
                    </p>
                    <div className={styles.columnTags}>
                      {section.columns.map((col, cIdx) => (
                        <span
                          key={cIdx}
                          className={clsx(
                            styles.columnTag,
                            styles[`tag_${section.severity}`]
                          )}
                        >
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              }
              return (
                <p key={sIdx} className={styles.errorGenericText}>
                  {section.text}
                </p>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

const NotificationBell = ({ onNotificationAction }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [onlyMine, setOnlyMine] = useState(false)
  const [activeTab, setActiveTab] = useState("all")
  const dropdownRef = useRef(null)
  const { institution } = useInstitution()
  const navigate = useNavigate()
  const location = useLocation()

  const fetchNotifications = useCallback(async () => {
    if (!institution?.id) return
    const data = await getNotifications(institution.id, { limit: 20, onlyMine })
    setNotifications(data.notifications || [])
    setUnreadCount(data.unreadCount || 0)
  }, [institution?.id, onlyMine])

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  const handleOutsideClick = useCallback(() => {
    setIsOpen(false)
  }, [])

  useOutsideAlerter(dropdownRef, handleOutsideClick)

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      fetchNotifications()
    }
  }

  const handleMarkAllRead = async () => {
    if (!institution?.id) return
    await markAllNotificationsAsRead(institution.id)
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    setUnreadCount(0)
  }

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markNotificationAsRead(notification.id)
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    }

    let data = null
    try {
      data = typeof notification.data === "string"
        ? JSON.parse(notification.data)
        : notification.data
    } catch { /* ignore */ }

    setSelectedNotification({ ...notification, parsedData: data })
    setIsOpen(false)
  }

  const handleModalAction = () => {
    if (!selectedNotification) return

    const { type, parsedData, id } = selectedNotification
    const plan = getActionPlan(selectedNotification)

    if (plan?.actionKind === "mapping" && onNotificationAction) {
      onNotificationAction({
        type,
        notificationId: id,
        data: parsedData
      })

      if (!location.pathname.includes("/importar-dados")) {
        navigate("/app/importar-dados")
      }
    } else if (plan?.actionKind === "navigate" && plan.actionUrl) {
      navigate(plan.actionUrl)
    } else if (plan?.actionKind === "goToImports") {
      if (!location.pathname.includes("/importar-dados")) {
        navigate("/app/importar-dados")
      }
    }

    setSelectedNotification(null)
  }

  const handleCloseModal = () => {
    setSelectedNotification(null)
  }

  const actionNotifications = notifications.filter((n) => getActionPlan(n) !== null)
  const unreadActionCount = actionNotifications.filter((n) => !n.isRead).length
  const visibleNotifications = activeTab === "actions" ? actionNotifications : notifications
  const selectedPlan = selectedNotification ? getActionPlan(selectedNotification) : null

  return (
    <div className={styles.bellContainer} ref={dropdownRef}>
      <div onClick={handleToggle}>
        <svg
          className={styles.bellIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={styles.badge}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>Notificações</span>
            <div className={styles.dropdownActions}>
              <button
                type="button"
                className={clsx(styles.filterOnlyMine, onlyMine && styles.filterOnlyMineActive)}
                onClick={() => setOnlyMine((v) => !v)}
                title={onlyMine ? "Mostrar todas da instituição" : "Mostrar só minhas notificações"}
              >
                Só minhas
              </button>
              {unreadCount > 0 && (
                <button
                  className={styles.markAllRead}
                  onClick={handleMarkAllRead}
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>
          </div>

          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "all"}
              className={clsx(styles.tab, activeTab === "all" && styles.tabActive)}
              onClick={() => setActiveTab("all")}
            >
              Todas
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "actions"}
              className={clsx(styles.tab, activeTab === "actions" && styles.tabActive)}
              onClick={() => setActiveTab("actions")}
            >
              {"Ação necessária"}
              {unreadActionCount > 0 && (
                <span className={styles.tabBadge}>{unreadActionCount}</span>
              )}
            </button>
          </div>

          <div className={styles.notificationList}>
            {visibleNotifications.length === 0 ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>
                  {activeTab === "actions" ? "\u2705" : "\uD83D\uDD14"}
                </span>
                <span className={styles.emptyText}>
                  {activeTab === "actions"
                    ? "Nenhuma ação pendente"
                    : "Nenhuma notificação"}
                </span>
              </div>
            ) : (
              visibleNotifications.map((n) => {
                const iconInfo = getIconInfo(n.type)
                const plan = getActionPlan(n)
                return (
                  <div
                    key={n.id}
                    className={clsx(
                      styles.notificationItem,
                      !n.isRead && styles.unread
                    )}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div
                      className={clsx(
                        styles.notificationIcon,
                        styles[iconInfo.className]
                      )}
                    >
                      {iconInfo.emoji}
                    </div>
                    <div className={styles.notificationContent}>
                      <p className={styles.notificationTitle}>{n.title}</p>
                      {n.userDisplayName && (
                        <span className={styles.notificationOwner}>
                          {n.userDisplayName}
                        </span>
                      )}
                      <p className={styles.notificationMessage}>{n.message}</p>
                      {plan && (
                        <span className={styles.planHint}>{plan.summary}</span>
                      )}
                      <span className={styles.notificationTime}>
                        {formatTimeAgo(n.createdAt)}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}

      {selectedNotification && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div
            className={styles.modalContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <span
                  className={clsx(
                    styles.notificationIcon,
                    styles[getIconInfo(selectedNotification.type).className]
                  )}
                >
                  {getIconInfo(selectedNotification.type).emoji}
                </span>
                <h3 className={styles.modalTitle}>
                  {selectedNotification.title}
                </h3>
              </div>
              <button
                className={styles.modalClose}
                onClick={handleCloseModal}
                aria-label="Fechar"
              >
                &times;
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.sectionLabel}>Descrição</p>
              {selectedNotification.type === "IMPORT_ERROR" ? (
                <ImportErrorDetails
                  parsedData={selectedNotification.parsedData}
                  message={selectedNotification.message}
                  userId={selectedNotification.userId}
                />
              ) : (
                <NotificationMessage
                  text={selectedNotification.message}
                  className={styles.modalMessage}
                />
              )}
              <NotificationDisciplines
                disciplines={selectedNotification.parsedData?.disciplines}
              />
              {selectedPlan && (
                <>
                  <p className={styles.sectionLabel}>Plano de ação</p>
                  <ol className={styles.planSteps}>
                    {selectedPlan.steps.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                </>
              )}
              <span className={styles.modalTime}>
                {formatTimeAgo(selectedNotification.createdAt)}
              </span>
            </div>

            <div className={styles.modalFooter}>
              {selectedPlan && (
                <button
                  className={styles.modalActionButton}
                  onClick={handleModalAction}
                >
                  {selectedPlan.actionLabel}
                </button>
              )}
              <button
                className={styles.modalCloseButton}
                onClick={handleCloseModal}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default NotificationBell
